import React from 'react';
import { 
  PlusIcon,
  CommandLineIcon,
  PencilIcon,
  TrashIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { BoltIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

export default function APIAccess() {
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 px-10 py-12 overflow-y-auto">
        <div className="max-w-[1400px] mx-auto flex flex-col gap-8">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-2">
            <div>
              <h1 className="text-[32px] font-extrabold text-slate-900 tracking-tight">API Access</h1>
              <p className="text-[16px] text-slate-500 mt-1 font-medium">
                Manage your keys, monitor consumption, and explore endpoints.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button className="flex items-center gap-2 bg-slate-100 border border-slate-200 text-slate-700 px-5 py-2.5 rounded-xl text-[14px] font-bold hover:bg-slate-200 transition-colors shadow-sm">
                <CommandLineIcon className="w-4 h-4 text-slate-500" /> CLI Docs
              </button>
              <button className="flex items-center gap-2 bg-brand-500 text-white px-6 py-2.5 rounded-xl text-[14px] font-bold hover:bg-brand-600 transition-colors shadow-lg shadow-brand-500/25">
                <PlusIcon className="w-5 h-5" /> Create New Key
              </button>
            </div>
          </div>

          {/* Top Row Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            
            {/* Usage Monitoring Chart */}
            <div className="xl:col-span-8 bg-white border border-slate-200 rounded-[24px] p-8 shadow-sm flex flex-col">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-[16px] font-bold text-slate-900">Usage Monitoring</h3>
                  <p className="text-[12px] text-slate-500">Request volume across all endpoints (Last 24h)</p>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-4 py-2 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                  <span className="text-[13px] font-bold text-slate-700">Last 24 Hours</span>
                  <ChevronDownIcon className="w-4 h-4 text-slate-400" />
                </div>
              </div>
              
              {/* Mock Bar Chart */}
              <div className="flex-1 relative w-full min-h-[220px] flex flex-col justify-end">
                {/* Tooltip for the active bar */}
                <div className="absolute top-[8%] left-[45.5%] -translate-x-1/2 bg-[#0f172a] text-white text-[11px] font-bold px-2.5 py-1.5 rounded-md shadow-md z-10">
                  2.8k
                </div>
                
                <div className="flex items-end justify-between h-[200px] gap-2 pb-6 border-b border-slate-100">
                  {/* Bars */}
                  <div className="w-full bg-slate-100 h-[30%]"></div>
                  <div className="w-full bg-slate-100 h-[50%]"></div>
                  <div className="w-full bg-slate-100 h-[55%]"></div>
                  <div className="w-full bg-slate-100 h-[80%]"></div>
                  <div className="w-full bg-brand-500 h-[100%] rounded-t-sm shadow-sm"></div>
                  <div className="w-full bg-slate-100 h-[85%]"></div>
                  <div className="w-full bg-slate-100 h-[65%]"></div>
                  <div className="w-full bg-slate-100 h-[40%]"></div>
                  <div className="w-full bg-slate-100 h-[35%]"></div>
                  <div className="w-full bg-slate-100 h-[60%]"></div>
                  <div className="w-full bg-slate-100 h-[70%]"></div>
                </div>
                
                {/* X-Axis Labels */}
                <div className="flex justify-between items-center mt-4 text-[10px] font-bold text-slate-500 tracking-widest px-2">
                  <span>00:00</span>
                  <span>06:00</span>
                  <span>12:00</span>
                  <span>18:00</span>
                  <span>23:59</span>
                </div>
              </div>
            </div>

            {/* Quick Start CURL Card */}
            <div className="xl:col-span-4 bg-[#0f172a] rounded-[24px] p-8 shadow-xl shadow-slate-900/10 text-white flex flex-col">
              <div className="flex items-center gap-2.5 mb-4">
                <BoltIcon className="w-5 h-5 text-white" />
                <h3 className="text-[16px] font-bold text-white">Quick Start CURL</h3>
              </div>
              <p className="text-[13px] text-slate-400 leading-relaxed mb-6">
                Instantly verify your connection. Replace <strong className="text-white">API_KEY</strong> with your credential below.
              </p>
              
              <div className="bg-[#1e293b] rounded-xl p-5 mb-6 border border-slate-700/50 shadow-inner overflow-x-auto">
                <pre className="text-[12px] font-mono text-slate-300 leading-loose">
                  <span className="text-pink-400">curl</span> -X POST \
                  <br />
                  <span className="text-amber-300">https://api.kinetic.ai/v1/analyze</span> \
                  <br />
                  &nbsp;&nbsp;-H <span className="text-emerald-300">"Authorization: Bearer </span><span className="text-brand-400">YOUR_KEY</span><span className="text-emerald-300">"</span> \
                  <br />
                  &nbsp;&nbsp;-d <span className="text-emerald-300">'{"{"}"query": "market_trends"{"}"}'</span>
                </pre>
              </div>

              <div className="flex flex-col gap-3 mt-auto">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-brand-500" />
                  <span className="text-[13px] text-slate-300 font-medium">Response Format: JSON</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-brand-500" />
                  <span className="text-[13px] text-slate-300 font-medium">Default Timeout: 30s</span>
                </div>
              </div>
            </div>

          </div>

          {/* Active Credentials Table */}
          <div className="bg-white border border-slate-200 rounded-[24px] shadow-sm flex flex-col overflow-hidden">
            <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-[16px] font-bold text-slate-900">Active Credentials</h3>
              <span className="px-3 py-1.5 bg-brand-50 text-brand-600 text-[12px] font-bold rounded-full">
                3 Active Keys
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left whitespace-nowrap">
                <thead>
                  <tr className="bg-slate-50/80 text-[11px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100">
                    <th className="px-8 py-4">Key Name</th>
                    <th className="px-8 py-4">Hidden Key String</th>
                    <th className="px-8 py-4">Created Date</th>
                    <th className="px-8 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {/* Key 1 */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-8 py-5">
                      <p className="text-[14px] font-bold text-slate-900">Production_Primary_Node</p>
                    </td>
                    <td className="px-8 py-5">
                      <code className="bg-slate-100 text-slate-500 text-[12px] px-3 py-1.5 rounded-md font-mono tracking-widest border border-slate-200/60">
                        kin_live_•••••••••••••••••3f92
                      </code>
                    </td>
                    <td className="px-8 py-5 text-[13px] text-slate-600 font-medium">
                      Oct 12, 2023
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="flex items-center justify-end gap-4">
                        <button className="text-slate-400 hover:text-brand-500 transition-colors">
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button className="text-slate-400 hover:text-red-500 transition-colors">
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Key 2 */}
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-8 py-5">
                      <p className="text-[14px] font-bold text-slate-900">Staging_Environment</p>
                    </td>
                    <td className="px-8 py-5">
                      <code className="bg-slate-100 text-slate-500 text-[12px] px-3 py-1.5 rounded-md font-mono tracking-widest border border-slate-200/60">
                        kin_test_•••••••••••••••••7a21
                      </code>
                    </td>
                    <td className="px-8 py-5 text-[13px] text-slate-600 font-medium">
                      Nov 05, 2023
                    </td>
                    <td className="px-8 py-5 text-right">
                      <div className="flex items-center justify-end gap-4">
                        <button className="text-slate-400 hover:text-brand-500 transition-colors">
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button className="text-slate-400 hover:text-red-500 transition-colors">
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom Row Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            
            {/* Core Endpoints */}
            <div className="xl:col-span-8 bg-white border border-slate-200 rounded-[24px] shadow-sm flex flex-col overflow-hidden">
              <div className="px-8 py-6 border-b border-slate-100">
                <h3 className="text-[16px] font-bold text-slate-900">Core Endpoints</h3>
              </div>

              <div className="overflow-x-auto flex-1">
                <table className="w-full text-left whitespace-nowrap">
                  <thead>
                    <tr className="bg-slate-50/80 text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-slate-100">
                      <th className="px-8 py-4">Method</th>
                      <th className="px-8 py-4">Path</th>
                      <th className="px-8 py-4">Description</th>
                      <th className="px-8 py-4 text-right">Credit Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {/* Endpoint 1 */}
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="px-8 py-5">
                        <span className="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-extrabold rounded-full">GET</span>
                      </td>
                      <td className="px-8 py-5 font-mono text-[13px] font-bold text-slate-900">/v1/health</td>
                      <td className="px-8 py-5 text-[13px] text-slate-500 font-medium">Check API heartbeat and status.</td>
                      <td className="px-8 py-5 text-right font-mono text-[13px] font-bold text-slate-900">0.0</td>
                    </tr>
                    
                    {/* Endpoint 2 */}
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="px-8 py-5">
                        <span className="px-3 py-1 bg-emerald-50 text-emerald-600 text-[10px] font-extrabold rounded-full">POST</span>
                      </td>
                      <td className="px-8 py-5 font-mono text-[13px] font-bold text-slate-900">/v1/analyze</td>
                      <td className="px-8 py-5 text-[13px] text-slate-500 font-medium whitespace-normal leading-snug w-64">Core ML processing for data arrays.</td>
                      <td className="px-8 py-5 text-right font-mono text-[13px] font-bold text-brand-600">1.6</td>
                    </tr>

                    {/* Endpoint 3 */}
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="px-8 py-5">
                        <span className="px-3 py-1 bg-emerald-50 text-emerald-600 text-[10px] font-extrabold rounded-full">POST</span>
                      </td>
                      <td className="px-8 py-5 font-mono text-[13px] font-bold text-slate-900">/v1/batch_ingest</td>
                      <td className="px-8 py-5 text-[13px] text-slate-500 font-medium whitespace-normal leading-snug w-64">High-volume data cluster upload.</td>
                      <td className="px-8 py-5 text-right font-mono text-[13px] font-bold text-brand-600">2.4</td>
                    </tr>

                    {/* Endpoint 4 */}
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="px-8 py-5">
                        <span className="px-3 py-1 bg-amber-50 text-amber-600 text-[10px] font-extrabold rounded-full">PATCH</span>
                      </td>
                      <td className="px-8 py-5 font-mono text-[13px] font-bold text-slate-900">/v1/model/tuning</td>
                      <td className="px-8 py-5 text-[13px] text-slate-500 font-medium">Update hyper-parameters.</td>
                      <td className="px-8 py-5 text-right font-mono text-[13px] font-bold text-brand-600">0.8</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Live Logs */}
            <div className="xl:col-span-4 bg-white border border-slate-200 rounded-[24px] shadow-sm flex flex-col p-6 overflow-hidden h-[450px]">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-[16px] font-bold text-slate-900">Live Logs</h3>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Streaming</span>
                </div>
              </div>

              <div className="flex-1 flex flex-col gap-3 overflow-y-auto mb-4 pr-1">
                {/* Log Item 1 */}
                <div className="p-4 border border-slate-100 rounded-xl bg-slate-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-extrabold text-emerald-600 uppercase tracking-widest">200 OK</span>
                    <span className="text-[10px] font-mono text-slate-400">12:44:02.32</span>
                  </div>
                  <p className="text-[13px] font-mono text-slate-700 font-bold mb-1">POST /v1/analyze</p>
                  <p className="text-[11px] text-slate-500">Latency: 142ms</p>
                </div>

                {/* Log Item 2 */}
                <div className="p-4 border border-slate-100 rounded-xl bg-slate-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-extrabold text-emerald-600 uppercase tracking-widest">200 OK</span>
                    <span className="text-[10px] font-mono text-slate-400">12:43:58.11</span>
                  </div>
                  <p className="text-[13px] font-mono text-slate-700 font-bold mb-1">GET /v1/health</p>
                  <p className="text-[11px] text-slate-500">Latency: 45ms</p>
                </div>

                {/* Log Item 3 (Error) */}
                <div className="p-4 border border-red-100 rounded-xl bg-red-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-extrabold text-red-600 uppercase tracking-widest">429 ERROR</span>
                    <span className="text-[10px] font-mono text-slate-500">12:43:45.90</span>
                  </div>
                  <p className="text-[13px] font-mono text-slate-800 font-bold mb-1">POST /v1/analyze</p>
                  <p className="text-[11px] font-medium text-red-500">Rate Limit Exceeded</p>
                </div>

                {/* Log Item 4 */}
                <div className="p-4 border border-slate-100 rounded-xl bg-slate-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-extrabold text-emerald-600 uppercase tracking-widest">200 OK</span>
                    <span className="text-[10px] font-mono text-slate-400">12:42:12.01</span>
                  </div>
                  <p className="text-[13px] font-mono text-slate-700 font-bold mb-1">PATCH /v1/model/tuning</p>
                  <p className="text-[11px] text-slate-500">Latency: 312ms</p>
                </div>
              </div>

              <button className="w-full py-3 bg-white border border-slate-200 text-slate-700 rounded-xl text-[13px] font-bold hover:bg-slate-50 transition-colors shadow-sm shrink-0">
                View Full Logs
              </button>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}